/* eslint-disable no-var */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../../API/axiosNeatex';
import type { APIResponse } from '../../../Shared/shared';

// =========================
// Helpers
// =========================
const toNum = (v: any) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

// const getItemKey = (row: any) => row?.transactionItemId ?? row?.itemId ?? row?.ItemId ?? row?.id ?? null;

const getItemId = (row: any) => row?.transactionItemId ?? row?.itemId ?? row?.ItemId ?? row?.id ?? null;

const getUomId = (row: any) => row?.transactionUom ?? row?.uom ?? row?.UomId ?? null;

// ✅ مفتاح مركّب Item|Uom
const makeDispenseKey = (itemId: any, uomId: any) => `${String(itemId)}|${String(uomId ?? 'NA')}`;

// =========================
// ✅ جلب الكمية المصروفة/المعالجة حسب المرجع
// ✅ FIX: جمع الكميات لنفس الصنف بدل الاستبدال
// =========================

export const fetchDispensedQuantities = createAsyncThunk(
  'Inv/fetchDispensedQuantities',
  async (referenceId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Inv/GetTransactionsListCountByReferenceId?ReferenceId=${referenceId}`);

      if (!data?.succeeded) return {};

      const quantities: Record<string, number> = {};

      (data.data || []).forEach((row: any) => {
        const itemId = getItemId(row);
        if (itemId == null) return;

        const uomId = getUomId(row);
        const qty = toNum(row?.quantity ?? row?.qty ?? 0);

        // ✅ 1) حسب (Item+UOM)
        const k2 = makeDispenseKey(itemId, uomId);
        quantities[k2] = (quantities[k2] ?? 0) + qty;

        // ✅ 2) احتياطي حسب Item فقط (لو UOM ناقصة في طرف آخر)
        const k1 = String(itemId);
        quantities[k1] = (quantities[k1] ?? 0) + qty;
      });

      return quantities;
    } catch (error) {
      console.error('Error fetching dispensed quantity:', error);
      return rejectWithValue('Failed to fetch dispensed quantities');
    }
  }
);

// =========================
// ✅ جلب البيانات حسب المرجع + تجهيز البنود مع Remaining/Processed صحيح
// =========================
export const fetchDataByReferenceId = createAsyncThunk(
  'Inv/fetchDataByReferenceId',
  async ({ referenceId, code }: { referenceId: string; code: string }, { dispatch, rejectWithValue }) => {
    const urls: Record<string, string> = {
      INV: `SalesInvoice/GetTransactionsInvoiceById?Id=${referenceId}`,

      // SOI: `SalesOrderSalse/GetTransactionsOrderSalseById?Id=${referenceId}`,

      SOI: `Sals/GetSalTransactionsByIdExpandedComponents?Id=${referenceId}&TransactionsTypesId=0997AD93-8418-41A0-B1B4-303C23070800`,

      // CRT: `Sals/GetTransactionsById?Id=${referenceId}`,
      CRT: `Sals/GetSalTransactionsByIdExpandedComponents?Id=${referenceId}&TransactionsTypesId=2A199F5A-4BAF-477F-9FB6-B2CD222A841C`,
      CRP: `Sals/GetSalTransactionsByIdExpandedComponents?Id=${referenceId}&TransactionsTypesId=8BAD731A-1A3E-4333-BDDF-60303FBC3E1E`,
      // https://api.neatix.app/api/Sals/GetSalTransactionsByIdExpandedComponents?Id=3fa85f64-5717-4562-b3fc-2c963f66afa6&TransactionsTypesId=3fa85f64-5717-4562-b3fc-2c963f66afa6

      SOP: `Sals/GetSalTransactionsByIdExpandedComponents?Id=${referenceId}&TransactionsTypesId=B3BFDD40-5B3F-4A69-8A7B-1EA47898310C`,

      // مردودات المشتريات
      SPR: `ReturnsPurchase/GetById?Id=${referenceId}`,
      SRP: `ReturnsPurchaseOrder/GetById?Id=${referenceId}`,

      BIL: `PurchaseBill/GetById?Id=${referenceId}`,

      POR: `PurchaseOrder/GetWithInventoryItemById?Id=${referenceId}`,

      MOI: `InvMove/GetMoveTransactionsById?Id=${referenceId}`, //  صرف امر تحويـل
      MOR: `InvIssue/GetIssueTransactionsById?Id=${referenceId}`, //  استلام امر تحويـل

      WOI: `Fleet/GetEquipmentsWorkOrderById?Id=${referenceId}` // امر صيانة
    };

    try {
      if (!urls[code]) {
        return rejectWithValue(`Unsupported code: ${code}`);
      }

      // ✅ 1) جلب الكميات المصروفة (مجمعة)
      const dispensedQuantities: Record<string, number> = await dispatch(
        fetchDispensedQuantities(referenceId)
      ).unwrap();

      // ✅ 2) جلب بيانات المعاملة
      const { data } = await ntxAPI.get(urls[code]);
      if (!data?.succeeded) return rejectWithValue('Failed to fetch data');

      const transactionData = data?.data ?? {};
      let itemsList: any[] = [];
      let result: any[] = [];

      if (code === 'WOI') {
        itemsList = transactionData.equipmentsWorkOrderItemList ?? [];
        result = itemsList.map((m: any) => {
          const itemId = m.itemId;
          const uomId = m.uom; // انت تستخدمها كـ uom
          const itemKey = String(itemId);

          const realQty = toNum(m.qty);

          // ✅ جرّب المركب أولاً ثم fallback
          const dispensedQuantity = toNum(
            dispensedQuantities[makeDispenseKey(itemId, uomId)] ?? dispensedQuantities[itemKey] ?? 0
          );

          const remainingQty = Math.max(0, realQty - dispensedQuantity);
          const isFully = dispensedQuantity >= realQty;
          // debugger
          return {
            transactionItemId: itemId,
            transactionUom: m.uom,

            // 🔸 حسب رغبتك: هنا كنت تضع 0 في WOI
            transactionQuantityIn: remainingQty,
            // transactionQuantityIn: 0,

            unitPrice: m.unitCost,
            referenceIdMaster: transactionData.id,
            referenceIdLine: m.id,

            RealQty: realQty,
            dispensedQuantity,
            RemainingQty: remainingQty,
            IsFullyProcessed: isFully,

            // احتجته لتعدد المخازن
            inventoryId: m.inventoryId
          };
        });
      } else {
        itemsList = transactionData.transactionsLinesList ?? transactionData.materialTransactionsLineList ?? [];
      // debugger
        // if (code === 'POR') {
        if (['SOI', 'POR' ,'SPR' ,'SRP' ,'SOI' ,'SOP' ,'CRP', 'CRT'].includes(code)) {
          itemsList = itemsList.filter(f => f.itemType === 'InventoryItem');
        }
// debugger
        result = itemsList
          .map((m: any) => {
            // const itemId = m.transactionItemId ?? m.itemId;
            // const itemKey = String(itemId);

            // const realQty = toNum(m.transactionQuantityIn ?? m.qty);
            // const dispensedQuantity = toNum(dispensedQuantities[itemKey] ?? 0);

            // const remainingQty = Math.max(0, realQty - dispensedQuantity);
            // const isFully = dispensedQuantity >= realQty;

            const itemId = m.transactionItemId ?? m.itemId;
            const uomId = m.transactionUom ?? m.uom;
            const itemKey = String(itemId);

            const realQty = toNum(m.transactionQuantityIn ?? m.qty);

            const dispensedQuantity = toNum(
              dispensedQuantities[makeDispenseKey(itemId, uomId)] ?? dispensedQuantities[itemKey] ?? 0
            );

            const remainingQty = Math.max(0, realQty - dispensedQuantity);
            const isFully = dispensedQuantity >= realQty;

            return {
              transactionItemId: itemId,
              transactionUom: m.transactionUom ?? m.uom,

              // 🔸 حسب كودك القديم: تملأ المتبقي تلقائياً
              transactionQuantityIn: remainingQty,

              unitPrice: m.price ?? m.unitPrice,
              referenceIdMaster: transactionData.id,
              referenceIdLine: m.id,

              RealQty: realQty,
              dispensedQuantity,
              RemainingQty: remainingQty,
              IsFullyProcessed: isFully,

              inventoryId: m.inventoryId
            };
          })
          // استبعاد الأصناف غير المخزنية
          .filter((f: any) => f.transactionItemId != null);
      }
      // ✅ ترتيب: غير مكتمل أولاً، المكتمل آخر شيء
      // result.sort((a, b) => Number(!!a.IsFullyProcessed) - Number(!!b.IsFullyProcessed));

      return { result, data };
    } catch (error: any) {
      console.error('Error fetching data by reference ID:', error);
      return rejectWithValue(error?.message || 'Failed to fetch data');
    }
  }
);

// =========================
// إنشاء حساب مخزني
// =========================
export const inventoryCreateAccount = createAsyncThunk(
  'Inv/InventoryCreateAccount',
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<any>>('Inv/CreateAccount', item);
      if (data?.succeeded) return data;

      return rejectWithValue(data.message);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// =========================
// جلب الحركات الجاهزة للترحيل
// =========================
export const getTransactionsListToPost = createAsyncThunk(
  'InvIssue/getTransactionsListToPost',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get('Inv/GetTransactionsListToPost');
      if (data?.succeeded) return data.data;

      return rejectWithValue(data.message);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// =========================
// Slice
// =========================
const initialState: {
  loading: boolean;
  error: any | null;
  itemsList: any[];
  TransactionsListToPost: any[];
} = {
  itemsList: [],
  TransactionsListToPost: [],
  loading: false,
  error: null
};

const invSlice = createSlice({
  name: 'Inv',
  initialState,
  reducers: {
    cleanUpItemsList: state => {
      state.itemsList = [];
    }
  },
  extraReducers(builder) {
    builder
      // ===== fetchDataByReferenceId =====
      .addCase(fetchDataByReferenceId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDataByReferenceId.fulfilled, (state, action) => {
        state.loading = false;
        // debugger
        state.itemsList = action.payload?.result ?? [];
      })
      .addCase(fetchDataByReferenceId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error;
      })

      // ===== getTransactionsListToPost =====
      .addCase(getTransactionsListToPost.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTransactionsListToPost.fulfilled, (state, action) => {
        state.loading = false;
        state.TransactionsListToPost = action.payload ?? [];
      })
      .addCase(getTransactionsListToPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error;
      })

      // (اختياري) inventoryCreateAccount
      .addCase(inventoryCreateAccount.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(inventoryCreateAccount.fulfilled, state => {
        state.loading = false;
      })
      .addCase(inventoryCreateAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error;
      });
  }
});

export const { cleanUpItemsList } = invSlice.actions;
export default invSlice.reducer;
